/*
 * <simple-carousel> custom element.
 *
 * Used by sections/featured-collection.liquid in "simple carousel" mode.
 * Wraps a horizontal scroll track of product cards and provides:
 *   - small dash-style pagination indicators (one dot per product in
 *     the original cycle, so dot clicks move by a single product),
 *   - click-and-drag swipe with the mouse,
 *   - optional seamless infinite loop when the markup renders the
 *     product list three times (data-loop="true"),
 *   - a small symmetric "peek" of the previous/next card on both
 *     edges, anchored via scroll-padding-left so the snap positions
 *     stay correctly offset.
 *
 * Important: this element does NOT rely on `scroll-behavior: smooth`
 * being set in CSS. Using the CSS value would animate the initial
 * scrollLeft assignment that places the viewport in the middle copy,
 * which produced a visible "scrolls through on refresh" flash. All
 * animated scrolls go through scrollTo({behavior:'smooth'}) explicitly.
 *
 * Required structure inside the element:
 *   .simple-carousel__track   - the scrollable container
 *   .simple-carousel__dots    - empty container we populate
 *
 * Optional host attributes:
 *   data-loop="true"
 *   data-original-count="<int>"
 */
class SimpleCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.simple-carousel__track');
    this.dotsContainer = this.querySelector('.simple-carousel__dots');
    if (!this.track || !this.dotsContainer) return;

    // Stop horizontal flings/drags from chaining to the page (and from
    // triggering the browser's back-swipe gesture) when the track reaches
    // an edge - that chaining is what causes a hitch at the ends.
    this.track.style.overscrollBehaviorX = 'contain';

    this.slides = this.track.querySelectorAll('[id^="Slide-"]');
    if (this.slides.length < 2) return;

    this.isLoop = this.dataset.loop === 'true';
    const parsedCount = parseInt(this.dataset.originalCount, 10);
    this.originalCount = Number.isFinite(parsedCount) && parsedCount > 0
      ? parsedCount
      : (this.isLoop ? Math.floor(this.slides.length / 3) : this.slides.length);

    // How many copies of the product list are rendered, and which copy is the
    // middle one we keep the viewport anchored to. Derived from the DOM so it
    // adapts if the copy count ever changes.
    this._copies = Math.max(1, Math.round(this.slides.length / this.originalCount));
    this._middleCopy = this.isLoop ? Math.floor(this._copies / 2) : 0;

    // Measure + position before announcing readiness, so the CSS
    // opacity gate stays at 0 until the carousel is already in its
    // final initial position. The very first painted frame the user
    // sees already shows the middle copy with symmetric peek - no
    // shake, no run-through.
    this._measure();
    this._positionToMiddle();
    this._renderDots();
    this._initScroll();
    this._initDrag();
    this._initDotsDrag();
    this._initResize();
    this.classList.add('simple-carousel--ready');
  }

  _measure() {
    const first = this.slides[0];
    const second = this.slides[1];
    this.slideOffset = Math.max(1, second.offsetLeft - first.offsetLeft);
    const fit = this.track.clientWidth / this.slideOffset;
    this.slidesPerView = Math.max(1, Math.floor(fit + 0.05));

    // Optional "centered single" mode. Below the configured viewport width we
    // force exactly one card per view so the adjacent cards render as large,
    // symmetric peeks (the mobile CSS sizes the card so each peek is ~3/5 of a
    // card). Without this, floor() above would treat the ~2.2 cards that fit as
    // two full cards and centre two instead of one.
    const centerMax = parseInt(this.dataset.centerSingleMax, 10);
    if (Number.isFinite(centerMax) && window.innerWidth <= centerMax) {
      this.slidesPerView = 1;
    }

    // Symmetric peek: split the spare viewport space between left and
    // right. We use the actually-measured slide width (first slide
    // bounding rect minus its left gap proxy = slideOffset minus
    // computed column-gap). For our use-case the difference between
    // "slide width" and "slide offset" is tiny so using slideOffset
    // here is close enough and avoids reading computed style every
    // resize.
    const sparePx = this.track.clientWidth - this.slidesPerView * this.slideOffset;
    this.peekOffset = Math.max(0, sparePx / 2);
    // The visible left/right peek would otherwise be asymmetric by one
    // column-gap (the right peek ends up one gap wider), because slideOffset
    // includes the gap but the row of full cards has one fewer gap than the
    // offset count assumes. Shifting the rest position by half a gap makes the
    // partial cards at both edges get cut equally.
    const cardWidth = first.getBoundingClientRect().width;
    this.gap = Math.max(0, this.slideOffset - cardWidth);
    this.restOffset = this.peekOffset + this.gap / 2;

    // EXACT width of one rendered copy, measured from the offset between the same
    // product in adjacent copies. This is NOT the same as originalCount*slideOffset:
    // sub-pixel rounding makes individual slide offsets vary (e.g. 268 vs 269), so
    // n*slideOffset can be a few px short of a true copy. Re-anchoring by this exact
    // value keeps the loop jump pixel-identical (invisible); using the approximation
    // produced a small visible "hard cut" each loop.
    this.copyWidth = (this.isLoop && this.slides.length > this.originalCount)
      ? (this.slides[this.originalCount].offsetLeft - this.slides[0].offsetLeft)
      : (this.originalCount * this.slideOffset);
    // peekOffset is applied to scrollLeft in _positionToMiddle and
    // _goToDot so the symmetric peek is visible at load-time and after
    // a dot click. With CSS scroll-snap disabled, free scrolling and
    // dragging will leave the carousel wherever the user releases it
    // - which is the requested behaviour.
  }

  _positionToMiddle() {
    if (!this.isLoop) return;
    // Direct assignment, no smooth scroll. Sets the viewport at the
    // first slide of the middle copy minus the peek offset so the
    // visual peek is symmetric from the very first frame.
    this.track.scrollLeft = (this._middleCopy * this.copyWidth) - this.restOffset;
    this._balancePeek();
  }

  _balancePeek() {
    // Fine-tune the scroll position so the partial cards peeking at the LEFT
    // and RIGHT edges are cut to the same width. The gap-based restOffset gets
    // it close; this measures the actual rendered peeks and removes any residual.
    //
    // Direction matters: leftPeek = (left card's right edge) - viewport.left, so
    // it SHRINKS as scrollLeft grows; rightPeek = viewport.right - (right card's
    // left edge), so it GROWS as scrollLeft grows. To equalise when the right peek
    // is larger (d = right - left > 0) we must shift content rightwards, i.e.
    // DECREASE scrollLeft by d/2 (and vice-versa). Using += here pushes away from
    // balance, and because the ResizeObserver re-invokes this every tick the error
    // compounds - that produced the ~one-gap skew we saw. The sign must be -=.
    const rect = this.track.getBoundingClientRect();
    let left = null;
    let right = null;
    for (let i = 0; i < this.slides.length; i++) {
      const b = this.slides[i].getBoundingClientRect();
      if (b.width < 5) continue;
      if (left === null && b.left < rect.left - 0.5 && b.right > rect.left + 0.5) {
        left = b.right - rect.left;
      }
      if (right === null && b.left < rect.right - 0.5 && b.right > rect.right + 0.5) {
        right = rect.right - b.left;
      }
    }
    if (left !== null && right !== null) {
      this.track.scrollLeft -= (right - left) / 2;
    }
  }

  _renderDots() {
    this.dotsContainer.innerHTML = '';
    this._progressThumb = null;
    const dotCount = this.originalCount;
    if (dotCount <= 1) {
      this.dotsContainer.hidden = true;
      return;
    }
    this.dotsContainer.hidden = false;

    const dots = [];
    for (let i = 0; i < dotCount; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'simple-carousel__dot';
      dot.dataset.index = String(i);
      dot.setAttribute('aria-label', `Go to product ${i + 1} of ${dotCount}`);
      dot.addEventListener('click', () => this._goToDot(i));
      this.dotsContainer.appendChild(dot);
      dots.push(dot);
    }

    // If one dash per product wraps onto more than one row (many products on a
    // narrow/phone screen), swap the dashes for a single compact progress track
    // with a sliding thumb. It stays on one row for ANY product count and keeps
    // the same minimalist aesthetic: one dash gliding along a faint line. When
    // the dashes fit on one row we leave them as-is (and they remain clickable).
    const wrapped = dots[dots.length - 1].offsetTop > dots[0].offsetTop + 1;
    if (wrapped) {
      this.dotsContainer.innerHTML = '';
      const track = document.createElement('div');
      track.className = 'simple-carousel__progress';
      const thumb = document.createElement('div');
      thumb.className = 'simple-carousel__progress-thumb';
      track.appendChild(thumb);
      this.dotsContainer.appendChild(track);
      this._progressThumb = thumb;
      this._initProgressDrag(track);
    }
    this._updateActiveDot();
  }

  _initProgressDrag(track) {
    // Make the progress track navigable: tapping jumps to that position and
    // dragging scrubs continuously through the products. We map the pointer's
    // x within the track to a fraction 0..1, then scroll the carousel to the
    // matching slide in the middle (loop) copy. On release we settle on the
    // nearest product with a smooth scroll.
    const fracFromEvent = (e) => {
      const rect = track.getBoundingClientRect();
      const x = e.clientX - rect.left;
      return Math.min(1, Math.max(0, x / Math.max(1, rect.width)));
    };
    const scrubTo = (frac) => {
      const n = this.originalCount;
      // /n cyclic model - consistent with the swipe-driven thumb (_updateActiveDot)
      // so the thumb doesn't jump when the finger lifts. Maps bar fraction -> a
      // continuous position in the middle copy's product cycle.
      const targetSlide = this._middleCopy * n + frac * n;
      this.track.scrollLeft = targetSlide * this.slideOffset - this.restOffset;
      if (this._progressThumb) {
        this._progressThumb.style.left = (frac * 100) + '%';
        this._progressThumb.style.transform = 'translateX(' + (-frac * 100) + '%)';
      }
    };
    let active = false;
    const onDown = (e) => {
      active = true;
      this.isDragging = true; // suppress loop-reset while scrubbing
      this._scrubbing = true; // tell _updateActiveDot to leave the thumb to us
      if (this._inertiaRAF) {
        cancelAnimationFrame(this._inertiaRAF);
        this._inertiaRAF = null;
      }
      try { track.setPointerCapture(e.pointerId); } catch (_) {}
      track.classList.add('is-scrubbing');
      e.preventDefault();
      scrubTo(fracFromEvent(e));
    };
    const onMove = (e) => {
      if (!active) return;
      e.preventDefault();
      scrubTo(fracFromEvent(e));
    };
    const onUp = (e) => {
      if (!active) return;
      active = false;
      this.isDragging = false;
      this._scrubbing = false; // resume discrete thumb updates (settle to product)
      track.classList.remove('is-scrubbing');
      try { track.releasePointerCapture(e.pointerId); } catch (_) {}
      // Settle on the nearest product (within the middle copy) with a smooth
      // scroll. /n model + clamp keeps it inside the middle copy so the smooth
      // scroll doesn't cross a seam and trigger a re-anchor mid-animation.
      const n = this.originalCount;
      const product = Math.max(0, Math.min(n - 1, Math.round(fracFromEvent(e) * n)));
      const targetSlide = this._middleCopy * n + product;
      this._animateTo(targetSlide * this.slideOffset - this.restOffset);
    };
    track.addEventListener('pointerdown', onDown);
    track.addEventListener('pointermove', onMove);
    track.addEventListener('pointerup', onUp);
    track.addEventListener('pointercancel', onUp);
  }

  _initDotsDrag() {
    // Make the dash row (laptop view) grabbable: dragging horizontally across the
    // dashes scrubs the carousel, and the active (black) dash follows. A plain
    // click still navigates to that dash (we only suppress the click if a real
    // drag happened). Bound ONCE to the persistent dots container; it bows out
    // when the container is in progress-bar mode (that has its own drag).
    const container = this.dotsContainer;
    let active = false;
    let moved = false;
    let startX = 0;
    // Generous threshold so a normal click/tap (which often jitters a few px,
    // especially on a trackpad) still counts as a click and navigates, rather
    // than being mistaken for a drag and having its click suppressed.
    const THRESH = 8;

    const fracFromEvent = (e) => {
      const ds = container.querySelectorAll('.simple-carousel__dot');
      if (ds.length < 2) return 0;
      const left = ds[0].getBoundingClientRect().left;
      const right = ds[ds.length - 1].getBoundingClientRect().right;
      return Math.min(1, Math.max(0, (e.clientX - left) / Math.max(1, right - left)));
    };
    const scrubTo = (frac) => {
      const n = this.originalCount;
      // Map the fraction across the dash row to a continuous product position in
      // the middle copy (dash 0 -> product 0 ... last dash -> product n-1).
      const product = frac * (n - 1);
      this.track.scrollLeft = (this._middleCopy * n + product) * this.slideOffset - this.restOffset;
    };

    const onMove = (e) => {
      if (!active) return;
      if (!moved && Math.abs(e.clientX - startX) > THRESH) {
        moved = true;
        this.isDragging = true;   // only NOW is it a real drag (a click never sets this)
        this._cancelInertia();
        container.classList.add('is-scrubbing');
      }
      if (moved) {
        e.preventDefault();
        scrubTo(fracFromEvent(e));
      }
    };
    const onUp = (e) => {
      if (!active) return;
      active = false;
      this.isDragging = false;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      if (moved) {
        container.classList.remove('is-scrubbing');
        // Settle on the nearest product (stay in the middle copy) with a smooth scroll.
        const n = this.originalCount;
        const p = Math.max(0, Math.min(n - 1, Math.round(fracFromEvent(e) * (n - 1))));
        this._animateTo((this._middleCopy * n + p) * this.slideOffset - this.restOffset);
      }
    };
    const onDown = (e) => {
      if (this._progressThumb) return;          // progress-bar mode: it handles its own drag
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      active = true;
      moved = false;
      startX = e.clientX;
      // NOTE: isDragging / cancelInertia are set only once a real drag starts
      // (in onMove), so a plain click never flags a drag and its navigation fires.
      // Window-level listeners (no pointer capture) so the drag keeps working when
      // the cursor leaves the dash row, without retargeting a plain click away
      // from the dash (which would break click-to-navigate).
      window.addEventListener('pointermove', onMove, { passive: false });
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    };

    container.addEventListener('pointerdown', onDown);
    // Handle clicks for the WHOLE dot row (capture phase). The dashes are only
    // ~2px tall, so a real click almost always lands on the container around them
    // rather than precisely on a dash - that's why clicking appeared to do
    // nothing (the click target was the container, no dash handler ran). Navigate
    // from the click here: use the clicked dash's index if one was actually hit,
    // otherwise the nearest product from the click x. A click that was really a
    // drag is suppressed.
    container.addEventListener('click', (e) => {
      if (this._progressThumb) return;   // progress-bar mode handles its own taps
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
        return;
      }
      const n = this.originalCount;
      const dash = e.target.closest ? e.target.closest('.simple-carousel__dot') : null;
      let product;
      if (dash && dash.dataset.index != null) {
        product = parseInt(dash.dataset.index, 10);
      } else {
        product = Math.max(0, Math.min(n - 1, Math.round(fracFromEvent(e) * (n - 1))));
      }
      e.stopPropagation();               // don't let the dash's own handler double-fire
      this._goToDot(product);
    }, true);
  }

  _currentSlideIndex() {
    // Add peekOffset so the snap position (scrollLeft = N*offset -
    // peekOffset) maps cleanly back to slide N.
    return Math.round((this.track.scrollLeft + this.restOffset) / this.slideOffset);
  }

  _logicalIndex() {
    const raw = this._currentSlideIndex();
    const n = this.originalCount;
    return ((raw % n) + n) % n;
  }

  _goToDot(targetLogicalIndex) {
    // Stay in the user's current copy so dot clicks animate locally;
    // the loop-reset logic pulls us back to the middle copy after
    // scroll settles if we drift to an outer copy.
    const currentSlide = this._currentSlideIndex();
    const n = this.originalCount;
    const copyStart = Math.floor(currentSlide / n) * n;
    const targetSlide = copyStart + targetLogicalIndex;
    this._animateTo(targetSlide * this.slideOffset - this.restOffset);
  }

  _updateActiveDot() {
    // Progress-track mode: position the thumb from the EXACT (continuous) scroll
    // position so it tracks the carousel perfectly in parallel - no per-product
    // stepping and no transition lag (the CSS has no transition on left/transform).
    // frac runs 0..1 across one product cycle and snaps back at the seam (instant,
    // since there's no transition to animate the 1 -> 0 jump).
    if (this._progressThumb) {
      const n = this.originalCount;
      const raw = (this.track.scrollLeft + this.restOffset) / this.slideOffset;
      // Position RELATIVE to the middle copy's first product. The infinite-loop
      // re-anchor keeps this within ~[0, n), so we deliberately do NOT use a
      // global "raw % n": that put the cycle's 1 -> 0 discontinuity exactly on
      // the first product, and sub-pixel rounding then flipped the thumb to the
      // far right edge on page load. Relative + clamp puts the seam at the wrap
      // (handled by the re-anchor) and pins the first product to frac 0 (left).
      let frac = (raw - this._middleCopy * n) / n;
      if (frac < 0) frac = 0; else if (frac > 1) frac = 1;
      this._progressThumb.style.left = (frac * 100) + '%';
      this._progressThumb.style.transform = 'translateX(' + (-frac * 100) + '%)';
      return;
    }
    const dots = this.dotsContainer.querySelectorAll('.simple-carousel__dot');
    if (!dots.length) return;
    const current = this._logicalIndex();
    dots.forEach((d, i) => d.classList.toggle('is-active', i === current));
  }

  _recenter() {
    // Bring the viewport back into the MIDDLE copy by jumping whole copies. The
    // jump is exactly k*copyWidth, so the visible content is pixel-identical
    // (invisible). Returns true if it jumped. Safe to call during our OWN custom
    // inertia (next frame reads the new scrollLeft) but NOT during native
    // momentum or an active finger drag, where setting scrollLeft hitches.
    if (!this.isLoop) return false;
    const n = this.originalCount;
    const idx = this._currentSlideIndex();
    const copyOfIdx = Math.floor(idx / n);
    if (copyOfIdx === this._middleCopy) return false;
    // Jump by the EXACT pixel offset between the current near-slide and its
    // equivalent product in the middle copy, so that product lands precisely
    // where it is now. A fixed copyWidth would be up to 1px off for some
    // products (slide offsets vary 268/269 due to sub-pixel rounding), which
    // showed as a tiny hard cut each loop; this per-slide delta is pixel-exact.
    const idxMid = idx - (copyOfIdx - this._middleCopy) * n;
    if (this.slides[idxMid] && this.slides[idx]) {
      this.track.scrollLeft += this.slides[idxMid].offsetLeft - this.slides[idx].offsetLeft;
    } else {
      this.track.scrollLeft += (this._middleCopy - copyOfIdx) * this.copyWidth;
    }
    return true;
  }

  _cancelInertia() {
    if (this._inertiaRAF) {
      cancelAnimationFrame(this._inertiaRAF);
      this._inertiaRAF = null;
    }
    if (this._scrollAnim) {
      cancelAnimationFrame(this._scrollAnim);
      this._scrollAnim = null;
    }
  }

  _animateTo(target) {
    // Smoothly scroll to an absolute scrollLeft by writing it every frame (rAF).
    // We deliberately do NOT use the native scrollTo({behavior:'smooth'}): on this
    // overflow-x container it's unreliable on some devices (the click navigation
    // appeared to "do nothing"), whereas direct per-frame scrollLeft writes work
    // (same mechanism as the inertia glide).
    this._cancelInertia();
    const start = this.track.scrollLeft;
    const dist = target - start;
    if (Math.abs(dist) < 1) { this.track.scrollLeft = target; return; }
    const dur = 340;
    const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic
    const step = () => {
      const t = Math.min(1, (performance.now() - t0) / dur);
      this.track.scrollLeft = start + dist * ease(t);
      if (t < 1) {
        this._scrollAnim = requestAnimationFrame(step);
      } else {
        this._scrollAnim = null;
      }
    };
    this._scrollAnim = requestAnimationFrame(step);
  }

  _loopManage() {
    // Seamless infinite loop across the seam WITHOUT a visible stop. The problem:
    // on many devices, setting scrollLeft mid-fling cancels the native momentum,
    // so re-anchoring during native momentum stops the carousel dead at the seam
    // (it stopped at the first product). Instead, the moment native momentum
    // carries us out of the middle copy, we capture its velocity, re-anchor
    // (which cancels the native fling - that's fine) and continue the glide with
    // our OWN inertia, which can re-anchor freely. Net effect: the motion runs
    // continuously across the loop point.
    if (!this.isLoop) return;
    if (this.isDragging || this._touchActive || this._inertiaRAF) return;
    const n = this.originalCount;
    const copyOfIdx = Math.floor(this._currentSlideIndex() / n);
    if (copyOfIdx === this._middleCopy) return;
    if (this._wheelActive) {
      // Trackpad / mouse-wheel scrolling: just re-anchor seamlessly (a jump by
      // one pixel-identical copy). Do NOT hand off to our own inertia here -
      // while the wheel is still driving the native scroll, the inertia fights
      // it and produced a recurring hitch at the loop seam each time around.
      this._recenter();
      return;
    }
    // Native touch momentum (no wheel): hand off to our own inertia so the glide
    // continues seamlessly across the seam.
    const v = this._scrollVelocity || 0;
    this._recenter();
    if (Math.abs(v) > 0.02) this._startInertia(v);
  }

  _maybeLoopReset() {
    // After-stop safety net: re-center only when nothing is actively driving the
    // scroll, so we never fight native momentum or a finger drag. Continuous
    // motion is kept seamless by the inertia hand-off in _loopManage.
    if (this.isDragging || this._touchActive || this._inertiaRAF) return;
    this._recenter();
  }

  _initScroll() {
    let rafId = null;
    let scrollEndTimer = null;
    this._scrollVelocity = 0;
    this._touchActive = false;
    let lastLeft = this.track.scrollLeft;
    let lastTime = performance.now();

    this.track.addEventListener('scroll', () => {
      const now = performance.now();
      this._lastScrollTime = now;
      const dt = now - lastTime;
      if (dt > 0) {
        this._scrollVelocity = (this.track.scrollLeft - lastLeft) / dt; // px/ms
        lastLeft = this.track.scrollLeft;
        lastTime = now;
      }
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        this._updateActiveDot();
        this._loopManage();
        rafId = null;
      });
      if (scrollEndTimer) clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(() => this._maybeLoopReset(), 120);
    });

    // Don't reposition while a finger is actively driving the native scroll -
    // setting scrollLeft then fights the browser and hitches. We only re-anchor
    // once the finger is up (native momentum -> inertia hand-off) or after the
    // scroll fully settles.
    this.track.addEventListener('touchstart', () => {
      this._touchActive = true;
      this._cancelInertia();
    }, { passive: true });
    this.track.addEventListener('touchend', () => {
      this._touchActive = false;
      // Take over a real fling with our own inertia right from the lift-off,
      // instead of letting native momentum run and handing off to our inertia
      // only when it crosses the loop seam. That mid-glide hand-off was what
      // produced the slight twitch at the last<->first jump. Our inertia is
      // authoritative (it re-writes scrollLeft each frame) and re-anchors the
      // loop seamlessly, so the whole glide - across the seam - is one smooth
      // continuous motion with no switch.
      // Only treat it as a fling if the scroll was still moving at lift-off.
      // If the finger paused before lifting (no scroll event for a while), the
      // stored velocity is stale - ignore it so a held-then-released touch
      // doesn't launch an unwanted glide.
      const fresh = (performance.now() - (this._lastScrollTime || 0)) < 90;
      const v = fresh ? (this._scrollVelocity || 0) : 0;
      if (Math.abs(v) > 0.12) this._startInertia(v);
    }, { passive: true });
    this.track.addEventListener('touchcancel', () => { this._touchActive = false; }, { passive: true });

    // Track trackpad / mouse-wheel scrolling. While wheeling, the loop is
    // re-anchored seamlessly WITHOUT our inertia hand-off (see _loopManage) -
    // the hand-off fought the native wheel scroll and hitched at the seam each
    // loop. The flag clears shortly after the last wheel event.
    let wheelTimer = null;
    this.track.addEventListener('wheel', () => {
      this._wheelActive = true;
      this._cancelInertia();
      if (wheelTimer) clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => { this._wheelActive = false; }, 160);
    }, { passive: true });
  }

  _initResize() {
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      // Re-measure and re-pin to the middle copy on every resize tick. Pinning
      // immediately (rather than debounced) keeps the carousel visually anchored
      // while the cards resize - debouncing let the view drift natively, which
      // looked like the products auto-scrolling/swiping past during a resize.
      this._measure();
      this._positionToMiddle();
      this._renderDots();
    });
    ro.observe(this.track);
  }

  _initDrag() {
    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = false;
    let samples = []; // recent {time, scrollLeft} pairs for velocity
    let pendingLeft = null; // latest target scrollLeft awaiting a frame
    let moveRAF = null;
    const DRAG_THRESHOLD = 5;
    const VELOCITY_WINDOW_MS = 100;
    this.isDragging = false;
    this._inertiaRAF = null;

    // Apply the most recent drag position once per animation frame. High-Hz
    // mice fire pointermove several times per frame; writing scrollLeft on
    // every event forces redundant scroll work and makes the drag feel
    // choppy. Coalescing to one write per frame keeps it smooth.
    const applyMove = () => {
      moveRAF = null;
      if (pendingLeft === null) return;
      this.track.scrollLeft = pendingLeft;
      const now = performance.now();
      samples.push({ time: now, scrollLeft: this.track.scrollLeft });
      while (samples.length > 2 && samples[0].time < now - VELOCITY_WINDOW_MS) {
        samples.shift();
      }
    };

    const cancelInertia = () => {
      if (this._inertiaRAF) {
        cancelAnimationFrame(this._inertiaRAF);
        this._inertiaRAF = null;
      }
    };

    const onPointerDown = (e) => {
      if (e.pointerType !== 'mouse') return;
      if (e.button !== 0) return;
      // Any new grab cancels an ongoing inertia glide so the user can
      // catch the carousel mid-flight.
      cancelInertia();
      isDown = true;
      moved = false;
      this.isDragging = true;
      startX = e.clientX;
      startScrollLeft = this.track.scrollLeft;
      samples = [{ time: performance.now(), scrollLeft: this.track.scrollLeft }];
      // NOTE: we do NOT add the 'is-dragging' class here. That class sets the
      // product links to pointer-events:none; doing it on pointerdown meant a
      // plain click landed on the card wrapper instead of the link, so clicks
      // never navigated. It is added in onPointerMove once a real drag starts.
      // IMPORTANT: do NOT use setPointerCapture here. Pointer capture
      // retargets the subsequent `click` event to the track instead of the
      // product link, which made the carousel cards unclickable. Instead we
      // track the drag with window-level listeners (added now, removed in
      // finishDrag) so the drag keeps working even when the cursor leaves
      // the track, without hijacking the click.
      window.addEventListener('pointermove', onPointerMove, { passive: false });
      window.addEventListener('pointerup', finishDrag);
      window.addEventListener('pointercancel', finishDrag);
    };

    const onPointerMove = (e) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > DRAG_THRESHOLD) {
        moved = true;
        // Only now that it's a real drag do we flag the track as dragging
        // (which disables link clicks + shows the grabbing cursor). A plain
        // click never reaches this, so its link stays clickable.
        this.track.classList.add('is-dragging');
      }
      if (moved) {
        // preventDefault must run in the event (not the rAF) to stop the
        // native text-selection / scroll; the actual scrollLeft write is
        // coalesced into applyMove so it happens at most once per frame.
        e.preventDefault();
        pendingLeft = startScrollLeft - dx;
        if (moveRAF === null) moveRAF = requestAnimationFrame(applyMove);
      }
    };

    const finishDrag = (e) => {
      if (!isDown) return;
      // Flush any drag position still waiting for a frame so the release
      // velocity is computed from the true final position.
      if (moveRAF !== null) {
        cancelAnimationFrame(moveRAF);
        moveRAF = null;
      }
      if (pendingLeft !== null) {
        this.track.scrollLeft = pendingLeft;
        pendingLeft = null;
      }
      isDown = false;
      this.isDragging = false;
      this.track.classList.remove('is-dragging');
      // Detach the window listeners that were active for this drag.
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', finishDrag);
      window.removeEventListener('pointercancel', finishDrag);
      // Smoother release: continue the scroll with a decaying velocity
      // so the carousel glides for a short moment instead of stopping
      // dead at the cursor position. The dots are still the only thing
      // that snaps to a slide boundary.
      if (moved && samples.length >= 2) {
        const last = samples[samples.length - 1];
        const first = samples[0];
        const dt = last.time - first.time;
        if (dt > 0) {
          const velocity = (last.scrollLeft - first.scrollLeft) / dt; // px per ms
          if (Math.abs(velocity) > 0.05) this._startInertia(velocity);
        }
      }
    };

    const onClickCapture = (e) => {
      if (moved) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
      }
    };

    this.track.addEventListener('pointerdown', onPointerDown);
    // pointermove / pointerup / pointercancel are bound on window only while
    // a drag is active (see onPointerDown / finishDrag) so the cursor can
    // leave the track mid-drag without us needing pointer capture.
    this.track.addEventListener('click', onClickCapture, true);
    // Suppress the browser's native image/link ghost-drag inside the track,
    // which would otherwise interrupt a mouse drag.
    this.track.addEventListener('dragstart', (e) => e.preventDefault());

    // A dot click should also kill any ongoing inertia, otherwise the
    // smooth scrollTo from _goToDot fights the glide.
    this.dotsContainer.addEventListener('pointerdown', cancelInertia, true);
  }

  _startInertia(initialVelocity) {
    // Velocity unit: pixels per millisecond. We drive an ABSOLUTE position and
    // write it every frame so this inertia is authoritative: it overrides any
    // residual native momentum (no double-speed glide) and lets us re-anchor the
    // infinite loop seamlessly mid-glide - a scrollLeft jump by a whole copy is
    // invisible, and the glide simply continues from the new position.
    this._cancelInertia();
    let velocity = initialVelocity;
    const minSpeed = 0.02;          // ~20 px/s cutoff
    const frictionPerMs = 0.0045;   // ~e^-0.0045/ms - settles in ~600ms
    let lastTime = performance.now();
    let pos = this.track.scrollLeft;

    const step = () => {
      const now = performance.now();
      const dt = now - lastTime;
      lastTime = now;
      pos += velocity * dt;
      this.track.scrollLeft = pos;
      // Seamless infinite-loop re-anchor during our own glide. If it jumped,
      // adopt the new (pixel-identical) position so the glide continues smoothly.
      if (this._recenter()) pos = this.track.scrollLeft;
      // Exponential decay: velocity *= exp(-friction * dt)
      velocity *= Math.exp(-frictionPerMs * dt);
      if (Math.abs(velocity) < minSpeed) {
        this._inertiaRAF = null;
        return;
      }
      this._inertiaRAF = requestAnimationFrame(step);
    };
    this._inertiaRAF = requestAnimationFrame(step);
  }
}

if (!customElements.get('simple-carousel')) {
  customElements.define('simple-carousel', SimpleCarousel);
}
